import React, { useState, useEffect, useRef } from 'react';
import { Play, Pause, Volume2, Box, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAudio } from '../context/AudioContext';
import { fetchMapById } from '../services/api';
import SplineMapWrapperIframe from './SplineMapWrapperIframe';

// Mobile Teaser Component - shows a link to /galerie-3d on mobile
const MobileTeaser = () => {
  return (
    <section 
      className="py-12 px-4"
      style={{ backgroundColor: '#1a1a1a' }}
      data-testid="mobile-3d-teaser"
    >
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="h-[1px] flex-1 bg-gradient-to-r from-transparent to-[#C6A75E]/40" />
          <div className="w-1.5 h-1.5 rotate-45 border border-[#C6A75E]/40" />
          <div className="h-[1px] flex-1 bg-gradient-to-l from-transparent to-[#C6A75E]/40" />
        </div>

        <h2 
          className="text-2xl text-center mb-3"
          style={{ 
            fontFamily: '"Cormorant Garamond", Georgia, serif',
            color: '#C6A75E',
          }}
        >
          Galerie 3D Interactivă
        </h2>

        <p 
          className="text-center text-sm mb-6 leading-relaxed"
          style={{ 
            fontFamily: '"EB Garamond", Georgia, serif',
            color: '#D0D0D0',
          }}
        >
          Explorează 3 hărți istorice într-un format 3D interactiv unic. Rotește, mărește și descoperă detaliile ascunse ale fiecărei piese din colecție.
        </p>

        {/* Preview Images */}
        <div className="flex justify-center gap-2 mb-6">
          <div className="w-20 h-20 rounded-lg overflow-hidden border border-[#C6A75E]/20">
            <img 
              src="https://customer-assets.emergentagent.com/job_f71459f5-332b-488f-a9b0-69ae068f31ee/artifacts/mtbhdbrc_NZ8_8927.jpg"
              alt="Transilvania"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="w-20 h-20 rounded-lg overflow-hidden border border-[#C6A75E]/20">
            <img 
              src="https://customer-assets.emergentagent.com/job_f71459f5-332b-488f-a9b0-69ae068f31ee/artifacts/umd943ci_NZ8_8871.jpg"
              alt="Tabula Europae"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="w-20 h-20 rounded-lg overflow-hidden border border-[#C6A75E]/20">
            <img 
              src="https://customer-assets.emergentagent.com/job_f71459f5-332b-488f-a9b0-69ae068f31ee/artifacts/oskzblqz_NZ8_8874.jpg"
              alt="Dacia"
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        {/* CTA Button */}
        <Link
          to="/galerie-3d"
          className="flex items-center justify-center gap-3 w-full py-4 rounded-lg transition-all hover:scale-[1.02]"
          style={{
            background: 'linear-gradient(135deg, #3A1818 0%, #4A1E1E 50%, #3A1818 100%)',
            border: '1px solid rgba(198, 167, 94, 0.3)',
          }}
          data-testid="mobile-3d-teaser-btn"
        >
          <Box size={20} className="text-[#C6A75E]" />
          <span 
            className="tracking-wider uppercase text-sm"
            style={{ 
              fontFamily: '"Cormorant Garamond", Georgia, serif',
              color: '#C6A75E',
              fontWeight: 600,
            }}
          >
            Deschide Galeria 3D
          </span>
          <ChevronRight size={18} className="text-[#C6A75E]" />
        </Link>

        {/* Footer decoration */}
        <div className="flex items-center gap-3 mt-6">
          <div className="h-[1px] flex-1 bg-gradient-to-r from-transparent to-[#C6A75E]/40" />
          <div className="w-1.5 h-1.5 rotate-45 border border-[#C6A75E]/40" />
          <div className="h-[1px] flex-1 bg-gradient-to-l from-transparent to-[#C6A75E]/40" />
        </div>
      </div>
    </section>
  );
};

// Mini Audio Player Component for first showcase
const MiniAudioPlayer = ({ audioTrack }) => {
  const { isPlaying, currentAudio, playAudio, pauseAudio } = useAudio();
  const isThisPlaying = isPlaying && currentAudio?.id === audioTrack.id;

  const handleToggle = () => {
    if (isThisPlaying) {
      pauseAudio();
    } else {
      // Stop all local audio players first
      localAudioRegistry.instances.forEach((instance, instanceId) => {
        if (instance.audioElement) {
          instance.audioElement.pause();
          instance.audioElement.currentTime = 0;
          instance.setIsPlaying(false);
        }
      });
      localAudioRegistry.currentPlaying = null;
      
      // Then play from context
      playAudio(audioTrack);
    }
  };

  return (
    <div 
      className="flex items-center gap-3 mb-6 p-3 rounded-lg cursor-pointer group transition-all hover:bg-white/5"
      style={{
        background: 'linear-gradient(135deg, rgba(198, 167, 94, 0.08) 0%, rgba(198, 167, 94, 0.03) 100%)',
        border: '1px solid rgba(198, 167, 94, 0.15)',
      }}
      onClick={handleToggle}
      data-testid="mini-audio-player"
    >
      {/* Play/Pause Button */}
      <button
        className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all"
        style={{
          background: isThisPlaying 
            ? 'linear-gradient(135deg, #C6A75E 0%, #A08040 100%)'
            : 'linear-gradient(135deg, rgba(198, 167, 94, 0.2) 0%, rgba(198, 167, 94, 0.1) 100%)',
          border: '1px solid rgba(198, 167, 94, 0.3)',
        }}
      >
        {isThisPlaying ? (
          <Pause size={16} className="text-black" />
        ) : (
          <Play size={16} className="text-[#C6A75E] ml-0.5" />
        )}
      </button>

      {/* Track Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <Volume2 size={14} className="text-[#C6A75E]/60 flex-shrink-0" />
          <span 
            className="text-xs uppercase tracking-wider text-[#C6A75E]/60"
            style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}
          >
            Rezumat Audio
          </span>
        </div>
        <p 
          className="text-sm truncate mt-0.5"
          style={{ 
            fontFamily: '"Cormorant Garamond", Georgia, serif',
            color: '#C6A75E',
          }}
        >
          {audioTrack.title}
        </p>
        <p className="text-xs text-gray-500">
          Ep. {audioTrack.episode} • {audioTrack.duration}
        </p>
      </div>

      {/* Animated sound wave indicator when playing */}
      {isThisPlaying && (
        <div className="flex items-center gap-0.5 mr-2">
          <div className="w-0.5 h-3 bg-[#C6A75E] rounded-full animate-pulse" style={{ animationDelay: '0ms' }} />
          <div className="w-0.5 h-4 bg-[#C6A75E] rounded-full animate-pulse" style={{ animationDelay: '150ms' }} />
          <div className="w-0.5 h-2 bg-[#C6A75E] rounded-full animate-pulse" style={{ animationDelay: '300ms' }} />
          <div className="w-0.5 h-5 bg-[#C6A75E] rounded-full animate-pulse" style={{ animationDelay: '450ms' }} />
        </div>
      )}
    </div>
  );
};

// Global registry for all local audio players to prevent simultaneous playback
const localAudioRegistry = {
  currentPlaying: null,
  instances: new Map(),
  
  register(id, audioElement, setIsPlaying) {
    this.instances.set(id, { audioElement, setIsPlaying });
  },
  
  unregister(id) {
    this.instances.delete(id);
    if (this.currentPlaying === id) {
      this.currentPlaying = null;
    }
  },
  
  play(id) {
    // Stop all other audio players first
    this.instances.forEach((instance, instanceId) => {
      if (instanceId !== id && instance.audioElement) {
        instance.audioElement.pause();
        instance.audioElement.currentTime = 0;
        instance.setIsPlaying(false);
      }
    });
    
    // Play the requested audio
    const instance = this.instances.get(id);
    if (instance && instance.audioElement) {
      instance.audioElement.play();
      instance.setIsPlaying(true);
      this.currentPlaying = id;
    }
  },
  
  stop(id) {
    const instance = this.instances.get(id);
    if (instance && instance.audioElement) {
      instance.audioElement.pause();
      instance.setIsPlaying(false);
    }
    if (this.currentPlaying === id) {
      this.currentPlaying = null;
    }
  }
};

// Local Audio Player Component for 3D maps using local MP3 files
const LocalAudioPlayer = ({ audioSrc, title }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);
  const instanceId = useRef(`local-audio-${audioSrc}`);
  const { pauseAudio } = useAudio(); // Get pauseAudio from global context

  const handleToggle = () => {
    if (audioRef.current) {
      if (isPlaying) {
        localAudioRegistry.stop(instanceId.current);
      } else {
        // Also pause any audio from AudioContext (global player)
        pauseAudio();
        localAudioRegistry.play(instanceId.current);
      }
    }
  };

  useEffect(() => {
    const audio = audioRef.current;
    const id = instanceId.current;
    
    if (audio) {
      // Register this audio instance
      localAudioRegistry.register(id, audio, setIsPlaying);
      
      const handleEnded = () => {
        setIsPlaying(false);
        localAudioRegistry.currentPlaying = null;
      };
      audio.addEventListener('ended', handleEnded);
      
      return () => {
        audio.removeEventListener('ended', handleEnded);
        localAudioRegistry.unregister(id);
      };
    }
  }, [audioSrc]);

  return (
    <div 
      className="flex items-center gap-3 mb-6 p-3 rounded-lg cursor-pointer group transition-all hover:bg-white/5"
      style={{
        background: 'linear-gradient(135deg, rgba(198, 167, 94, 0.08) 0%, rgba(198, 167, 94, 0.03) 100%)',
        border: '1px solid rgba(198, 167, 94, 0.15)',
      }}
      onClick={handleToggle}
      data-testid="local-audio-player"
    >
      <audio ref={audioRef} src={audioSrc} preload="metadata" />
      {/* Play/Pause Button */}
      <button
        className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all"
        style={{
          background: isPlaying 
            ? 'linear-gradient(135deg, #C6A75E 0%, #A08040 100%)'
            : 'linear-gradient(135deg, rgba(198, 167, 94, 0.2) 0%, rgba(198, 167, 94, 0.1) 100%)',
          border: '1px solid rgba(198, 167, 94, 0.3)',
        }}
      >
        {isPlaying ? (
          <Pause size={16} className="text-black" />
        ) : (
          <Play size={16} className="text-[#C6A75E] ml-0.5" />
        )}
      </button>

      {/* Track Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <Volume2 size={14} className="text-[#C6A75E]/60 flex-shrink-0" />
          <span 
            className="text-xs uppercase tracking-wider text-[#C6A75E]/60"
            style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}
          >
            Rezumat Audio
          </span>
        </div>
        <p 
          className="text-sm truncate mt-0.5"
          style={{ 
            fontFamily: '"Cormorant Garamond", Georgia, serif',
            color: '#C6A75E',
          }}
        >
          {title}
        </p>
      </div>

      {/* Animated sound wave indicator when playing */}
      {isPlaying && (
        <div className="flex items-center gap-0.5 mr-2">
          <div className="w-0.5 h-3 bg-[#C6A75E] rounded-full animate-pulse" style={{ animationDelay: '0ms' }} />
          <div className="w-0.5 h-4 bg-[#C6A75E] rounded-full animate-pulse" style={{ animationDelay: '150ms' }} />
          <div className="w-0.5 h-2 bg-[#C6A75E] rounded-full animate-pulse" style={{ animationDelay: '300ms' }} />
          <div className="w-0.5 h-5 bg-[#C6A75E] rounded-full animate-pulse" style={{ animationDelay: '450ms' }} />
        </div>
      )}
    </div>
  );
};

// Section Divider Component
const SectionDivider = () => (
  <div className="relative h-16 w-full" style={{ background: '#0d0d0d' }}>
    {/* Top fade from section */}
    <div 
      className="absolute top-0 left-0 right-0 h-8"
      style={{
        background: 'linear-gradient(to bottom, #1a1a1a 0%, #0d0d0d 100%)',
      }}
    />
    
    {/* Center decorative line */}
    <div className="absolute top-1/2 left-0 right-0 -translate-y-1/2 flex items-center justify-center px-8">
      <div className="flex-1 h-px" style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(198, 167, 94, 0.3) 100%)' }} />
      <div className="mx-4 flex items-center gap-2">
        <div className="w-1.5 h-1.5 rotate-45 bg-[#C6A75E]/40" />
        <div className="w-2 h-2 rotate-45 border border-[#C6A75E]/40" />
        <div className="w-1.5 h-1.5 rotate-45 bg-[#C6A75E]/40" />
      </div>
      <div className="flex-1 h-px" style={{ background: 'linear-gradient(90deg, rgba(198, 167, 94, 0.3) 0%, transparent 100%)' }} />
    </div>
    
    {/* Bottom fade to next section */}
    <div 
      className="absolute bottom-0 left-0 right-0 h-8"
      style={{
        background: 'linear-gradient(to top, #1a1a1a 0%, #0d0d0d 100%)',
      }}
    />
  </div>
);

// Single 3D Showcase Section Component
const ShowcaseSection = ({ showcase, onMapClick, index, audioData }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const sectionRef = useRef(null);
  
  // Get audio track from AudioContext if audioTrackId is specified
  const audioTrack = showcase.audioTrackId ? audioData?.find(a => a.id === showcase.audioTrackId) : null;

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleViewDetails = async () => {
    if (onMapClick) {
      try {
        // Fetch full map data from API for complete description
        const fullMapData = await fetchMapById(showcase.id);
        onMapClick(fullMapData);
      } catch (error) {
        console.error('Error fetching map details:', error);
        // Fallback to local data if API fails
        onMapClick({
          id: showcase.id,
          title: showcase.title,
          year: showcase.year,
          author: showcase.author,
          region: showcase.region,
          imageUrl: showcase.imageUrl,
          description: showcase.fullDescription,
        });
      }
    }
  };

  return (
    <section 
      ref={sectionRef}
      className="showcase-section relative overflow-hidden" 
      style={{ backgroundColor: '#1a1a1a' }} 
      data-testid={`3d-showcase-${index}`}
    >
      {/* Content container - Desktop: side by side, Mobile: stacked */}
      <div className="showcase-content relative z-10 grid lg:grid-cols-2 lg:h-screen">
        
        {/* COLOANA STÂNGA - HARTA 3D */}
        <div className="relative h-screen">
          <SplineMapWrapperIframe 
            className="h-full w-full"
            style={{ position: 'sticky', top: 0 }}
            debugMode={false}
          >
            {/* Spline interaction hint for mobile */}
            {isMobile && (
              <div className="absolute top-4 left-4 right-4 z-10 pointer-events-none">
                <div 
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs"
                  style={{
                    background: 'rgba(0, 0, 0, 0.6)',
                    backdropFilter: 'blur(8px)',
                    color: '#C6A75E',
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 4h6v6M10 20H4v-6M21 3l-7 7M3 21l7-7"/>
                  </svg>
                  <span>Rotește și mărește harta 3D</span>
                </div>
              </div>
            )}

            {/* Spline interaction hint for desktop */}
            {!isMobile && (
              <div className="absolute bottom-4 left-4 z-10 pointer-events-none">
                <div 
                  className="flex flex-col gap-1 px-3 py-2 rounded-lg text-xs"
                  style={{
                    background: 'rgba(0, 0, 0, 0.7)',
                    backdropFilter: 'blur(8px)',
                    color: '#C6A75E',
                    border: '1px solid rgba(198, 167, 94, 0.3)',
                  }}
                >
                  <div className="flex items-center gap-2">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="3"/>
                      <path d="M12 2v4M12 18v4M2 12h4M18 12h4"/>
                    </svg>
                    <span>Scroll pentru zoom</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M14 4h6v6M10 20H4v-6M21 3l-7 7M3 21l7-7"/>
                    </svg>
                    <span>Click dreapta pentru a muta</span>
                  </div>
                </div>
              </div>
            )}
            
            {/* Spline iframe - always loads */}
            <iframe
              src={showcase.splineUrl}
              frameBorder="0"
              style={{ 
                width: '100%', 
                height: '100%', 
                border: 'none', 
                display: 'block',
                position: 'absolute',
                top: 0,
                left: 0,
                zIndex: 1
              }}
              title={`3D Historical Map - ${showcase.title}`}
              allow="autoplay; fullscreen; xr-spatial-tracking"
              loading="lazy"
              data-testid={`spline-embed-${index}`}
            />
          </SplineMapWrapperIframe>
        </div>

        {/* COLOANA DREAPTA - TEXT (cu scroll propriu) */}
        <div 
          className="panel-container overflow-y-auto h-screen flex items-center justify-center p-5 sm:p-8 lg:p-12 xl:p-16 relative"
          style={{ backgroundColor: '#1a1a1a' }}
        >
          
          {/* Animated dust particles overlay */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className={`dust-particle dust-1-${index}`} />
            <div className={`dust-particle dust-2-${index}`} />
            <div className={`dust-particle dust-3-${index}`} />
          </div>

          {/* Museum Panel Card */}
          <div 
            className={`
              relative max-w-xl w-full
              transition-all duration-700 ease-out
              ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
            `}
            style={{ transitionDelay: '0.2s' }}
            data-testid={`info-panel-${index}`}
          >
            {/* Dark panel background matching Spline */}
            <div 
              className="absolute inset-0 rounded-sm"
              style={{
                background: 'linear-gradient(135deg, rgba(30, 30, 30, 0.95) 0%, rgba(25, 25, 25, 0.98) 50%, rgba(28, 28, 28, 0.95) 100%)',
              }}
            />
            
            {/* Aged parchment grain overlay */}
            <div 
              className="absolute inset-0 rounded-sm opacity-[0.03]"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='paper'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='5' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23paper)'/%3E%3C/svg%3E")`,
              }}
            />

            {/* Vignette edge darkening */}
            <div 
              className="absolute inset-0 rounded-sm pointer-events-none"
              style={{
                boxShadow: 'inset 0 0 80px 20px rgba(0, 0, 0, 0.4)',
              }}
            />

            {/* Museum glass inset effect */}
            <div 
              className="absolute inset-0 rounded-sm"
              style={{
                boxShadow: `
                  inset 0 1px 0 rgba(255, 255, 255, 0.02),
                  inset 0 -1px 0 rgba(0, 0, 0, 0.3),
                  0 25px 50px -12px rgba(0, 0, 0, 0.5)
                `,
              }}
            />

            {/* Ornamental gold frame border */}
            <div 
              className="absolute inset-3 rounded-sm pointer-events-none"
              style={{
                border: '1px solid rgba(198, 167, 94, 0.2)',
                boxShadow: 'inset 0 0 20px rgba(0, 0, 0, 0.2)',
              }}
            />

            {/* Refined corner ornaments */}
            <div className="absolute top-5 left-5 w-6 h-6">
              <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-[#C6A75E]/50 to-transparent" />
              <div className="absolute top-0 left-0 h-full w-[1px] bg-gradient-to-b from-[#C6A75E]/50 to-transparent" />
              <div className="absolute top-1 left-1 w-1 h-1 bg-[#C6A75E]/30 rounded-full" />
            </div>
            <div className="absolute top-5 right-5 w-6 h-6">
              <div className="absolute top-0 right-0 w-full h-[1px] bg-gradient-to-l from-[#C6A75E]/50 to-transparent" />
              <div className="absolute top-0 right-0 h-full w-[1px] bg-gradient-to-b from-[#C6A75E]/50 to-transparent" />
              <div className="absolute top-1 right-1 w-1 h-1 bg-[#C6A75E]/30 rounded-full" />
            </div>
            <div className="absolute bottom-5 left-5 w-6 h-6">
              <div className="absolute bottom-0 left-0 w-full h-[1px] bg-gradient-to-r from-[#C6A75E]/50 to-transparent" />
              <div className="absolute bottom-0 left-0 h-full w-[1px] bg-gradient-to-t from-[#C6A75E]/50 to-transparent" />
              <div className="absolute bottom-1 left-1 w-1 h-1 bg-[#C6A75E]/30 rounded-full" />
            </div>
            <div className="absolute bottom-5 right-5 w-6 h-6">
              <div className="absolute bottom-0 right-0 w-full h-[1px] bg-gradient-to-l from-[#C6A75E]/50 to-transparent" />
              <div className="absolute bottom-0 right-0 h-full w-[1px] bg-gradient-to-t from-[#C6A75E]/50 to-transparent" />
              <div className="absolute bottom-1 right-1 w-1 h-1 bg-[#C6A75E]/30 rounded-full" />
            </div>

            {/* Content */}
            <div className="relative z-10 p-8 sm:p-10 lg:p-12">
              
              {/* Small decorative header element */}
              <div className="flex items-center gap-3 mb-6">
                <div className="h-[1px] w-6 bg-[#C6A75E]/40" />
                <div className="w-1.5 h-1.5 rotate-45 border border-[#C6A75E]/40" />
                <div className="h-[1px] w-6 bg-[#C6A75E]/40" />
              </div>

              {/* Title */}
              <h2 
                className="text-3xl sm:text-4xl lg:text-[2.5rem] font-semibold mb-2 tracking-[0.04em] leading-tight"
                style={{ 
                  fontFamily: '"Cormorant Garamond", "Playfair Display", Georgia, serif',
                  color: '#C6A75E',
                  textShadow: '0 2px 4px rgba(0, 0, 0, 0.5), 0 1px 0 rgba(0, 0, 0, 0.4)',
                }}
              >
                {showcase.title}
              </h2>

              {/* Subtitle */}
              {showcase.subtitle && (
                <p 
                  className="text-lg mb-4"
                  style={{ 
                    fontFamily: '"Cormorant Garamond", Georgia, serif',
                    color: '#A89060',
                    fontStyle: 'italic',
                  }}
                >
                  {showcase.subtitle}
                </p>
              )}
              
              {/* Year with divider */}
              <div className="mb-6">
                <div className="flex items-baseline gap-3 mb-3">
                  <span 
                    className="text-base tracking-[0.2em] uppercase"
                    style={{ 
                      fontFamily: '"Cormorant Garamond", Georgia, serif',
                      color: '#A89060',
                      fontWeight: 500,
                    }}
                  >
                    {showcase.year}
                  </span>
                  <span className="text-[#6B6B6B] text-xs tracking-widest uppercase">d.Hr.</span>
                </div>
                <div className="h-[1px] w-20 bg-gradient-to-r from-[#C6A75E]/30 via-[#C6A75E]/15 to-transparent" />
              </div>

              {/* Author */}
              <h3 
                className="text-lg sm:text-xl mb-5 tracking-wide"
                style={{ 
                  fontFamily: '"Cormorant Garamond", Georgia, serif',
                  color: '#B8A88A',
                  fontWeight: 400,
                  fontStyle: 'italic',
                }}
              >
                {showcase.author}
              </h3>

              {/* Local Audio Player - show if showcase has audioSrc */}
              {showcase.audioSrc && (
                <LocalAudioPlayer audioSrc={showcase.audioSrc} title={showcase.title} />
              )}
              
              {/* AudioContext Audio Player - show if showcase has audioTrackId and audioTrack exists */}
              {!showcase.audioSrc && audioTrack && (
                <MiniAudioPlayer audioTrack={audioTrack} />
              )}

              {/* Description */}
              <p 
                className="mb-8 leading-[1.8] max-w-md text-sm sm:text-base"
                style={{ 
                  fontFamily: '"EB Garamond", Georgia, serif',
                  color: '#D0D0D0',
                  fontWeight: 400,
                }}
              >
                {showcase.description}
              </p>

              {/* Museum metadata section */}
              <div className="mb-8">
                <div className="h-[1px] w-full bg-gradient-to-r from-[#C6A75E]/25 via-[#C6A75E]/15 to-[#C6A75E]/25 mb-4" />
                
                <div className="flex items-center justify-start gap-5 sm:gap-8">
                  <div className="text-center">
                    <div 
                      className="text-base mb-1 tracking-wider"
                      style={{ 
                        fontFamily: '"Cormorant Garamond", Georgia, serif',
                        color: '#C6A75E',
                        fontWeight: 600,
                      }}
                    >
                      {showcase.year}
                    </div>
                    <div className="text-[9px] uppercase tracking-[0.25em] text-[#6B6B6B]">An</div>
                  </div>
                  
                  <div className="w-[1px] h-6 bg-[#C6A75E]/15" />
                  
                  <div className="text-center">
                    <div 
                      className="text-base mb-1 tracking-wider capitalize"
                      style={{ 
                        fontFamily: '"Cormorant Garamond", Georgia, serif',
                        color: '#C6A75E',
                        fontWeight: 600,
                      }}
                    >
                      {showcase.region}
                    </div>
                    <div className="text-[9px] uppercase tracking-[0.25em] text-[#6B6B6B]">Regiune</div>
                  </div>
                  
                  <div className="w-[1px] h-6 bg-[#C6A75E]/15" />
                  
                  <div className="text-center">
                    <div 
                      className="text-base mb-1 tracking-wider"
                      style={{ 
                        fontFamily: '"Cormorant Garamond", Georgia, serif',
                        color: '#C6A75E',
                        fontWeight: 600,
                      }}
                    >
                      Rare
                    </div>
                    <div className="text-[9px] uppercase tracking-[0.25em] text-[#6B6B6B]">Colecție</div>
                  </div>
                </div>
                
                <div className="h-[1px] w-full bg-gradient-to-r from-[#C6A75E]/25 via-[#C6A75E]/15 to-[#C6A75E]/25 mt-4" />
              </div>

              {/* Premium CTA Button */}
              <button
                onClick={handleViewDetails}
                className="
                  group relative
                  px-6 py-3
                  transition-all duration-300 ease-out
                  hover:-translate-y-[2px]
                  focus:outline-none focus:ring-2 focus:ring-[#C6A75E]/30 focus:ring-offset-2 focus:ring-offset-[#1a1a1a]
                "
                style={{
                  background: 'linear-gradient(135deg, #3A1818 0%, #4A1E1E 50%, #3A1818 100%)',
                  border: '1px solid rgba(198, 167, 94, 0.3)',
                  borderRadius: '4px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.03)',
                }}
                data-testid={`view-details-btn-${index}`}
              >
                <div 
                  className="absolute inset-0 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{
                    boxShadow: '0 0 20px rgba(198, 167, 94, 0.15), 0 8px 25px rgba(58, 24, 24, 0.5)',
                  }}
                />
                
                <div className="relative flex items-center justify-center gap-3">
                  <span 
                    className="tracking-[0.12em] uppercase text-sm"
                    style={{ 
                      fontFamily: '"Cormorant Garamond", Georgia, serif',
                      color: '#C6A75E',
                      fontWeight: 600,
                    }}
                  >
                    Vezi Detalii Complete
                  </span>
                  <svg 
                    className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" 
                    fill="none" 
                    stroke="#C6A75E"
                    strokeWidth="1.5"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Main component that renders all 3 showcases as separate sections
const Interactive3DShowcase = ({ onMapClick }) => {
  const { audioData } = useAudio();
  const [isMobile, setIsMobile] = useState(false);
  
  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Scroll blocking is now handled by each ShowcaseSection individually
  // using mouseenter/mouseleave on containerRef

  // All 3D map showcases data with local audio files
  const showcases = [
    {
      id: 'NZ8_8927',
      splineUrl: 'https://my.spline.design/untitled-Ny6XkuQG25Wfmoohq9jvD9af/',
      title: '2 Transsylvania XXI Nova Tabula',
      subtitle: 'Gravură pe cupru - Johannes Sambucus',
      year: '1566',
      author: 'Johannes Sambucus',
      region: 'transilvania',
      imageUrl: 'https://customer-assets.emergentagent.com/job_f71459f5-332b-488f-a9b0-69ae068f31ee/artifacts/mtbhdbrc_NZ8_8927.jpg',
      description: 'Harta Transilvaniei publicată în anul 1566 la Viena, realizată de savantul umanist Johannes Sambucus, reprezintă o veritabilă capodoperă a cartografiei renascentiste.',
      fullDescription: 'Transilvania în Secolul al XVI-lea: O Analiză Exhaustivă a Hărții lui Johannes Sambucus și a Realităților Cotidiene, Demografice și Militare.',
      audioSrc: '/assets/audio/Transsylvania XXI Nova Tabula.mp3',
    },
    {
      id: 'NZ8_8871',
      splineUrl: 'https://my.spline.design/untitled-mZtG5n0fYxl65bXMDA6Yi56Z/',
      title: 'Tabula Europae IX',
      subtitle: 'Geographia Universalis',
      year: '1596',
      author: 'Giovanni Antonio Magini',
      region: 'dacia',
      imageUrl: 'https://customer-assets.emergentagent.com/job_f71459f5-332b-488f-a9b0-69ae068f31ee/artifacts/umd943ci_NZ8_8871.jpg',
      description: 'A noua tablă a Europei din Geografia lui Ptolemeu, publicată de Giovanni Antonio Magini. Reprezintă o proiecție a lumii antice, incluzând Dacia și regiunile carpato-danubiano-pontice.',
      fullDescription: 'Radiografia Spațiului Carpato-Danubiano-Pontic: O Analiză Istorică și Cartografică a hărții lui Giovanni Antonio Magini.',
      audioSrc: null, // Audio eliminat conform cerințelor
    },
    {
      id: 'Z8L_5030',  // Matches MongoDB ID for TABVLA EVROPAE IX
      splineUrl: 'https://my.spline.design/untitledcopy-zYgqEcp4YlfhPy4kGrDAfYuC/',
      title: 'TABVLA EVROPAE IX',
      subtitle: 'A noua hartă a Europei - Ptolemeu',
      year: '150 d.Hr. (tipărit 1559)',
      author: 'Claudius Ptolemeu (tipărit de Sebastian Münster)',
      region: 'dacia',
      imageUrl: '/maps/Z8L_5030.jpg',
      description: 'A noua tablă a Europei din Geografia lui Ptolemeu, reprezentând spațiul carpato-danubiano-pontic în viziunea geografiei antice.',
      fullDescription: 'TABVLA EVROPAE IX reprezintă a noua hartă a Europei din monumentala lucrare Geographia a lui Claudius Ptolemeu, tipărită de Sebastian Münster în 1559.',
      audioSrc: '/assets/audio/Dacia la Sec. II.mp3',
    },
    // NEW 3D Maps - IDs match MongoDB for full descriptions
    {
      id: 'NZ8_8921',  // Matches MongoDB ID
      splineUrl: 'https://my.spline.design/untitled-ipc7uTLYGXu4fH5HEYc0zpnO/',
      title: 'Turcici Imperii Descriptio',
      subtitle: 'Descrierea Imperiului Otoman',
      year: '1570',
      author: 'Abraham Ortelius',
      region: 'imperiul-otoman',
      imageUrl: 'https://customer-assets.emergentagent.com/job_f71459f5-332b-488f-a9b0-69ae068f31ee/artifacts/8t6xj8fu_NZ8_8921.jpg',
      description: 'O hartă detaliată a Imperiului Otoman, prezentând întinderea vastă a teritoriilor controlate de Sublima Poartă în perioada sa de maximă expansiune.',
      fullDescription: 'Turcici Imperii Descriptio reprezintă o hartă importantă care ilustrează dimensiunea și puterea Imperiului Otoman la apogeul său.',
      audioSrc: null,
      audioTrackId: 42, // Turcici Imperii Descriptio din AudioContext
    },
    {
      id: 'NZ8_8906',  // Matches MongoDB ID for full description
      splineUrl: 'https://my.spline.design/untitled-hVbuSVJsmo54o4DNkjcLYDdA/',
      title: 'Transsylvania XXI Nova Tabula (Ed. II)',
      subtitle: 'Ediția a Doua - Cosmographia',
      year: '1544',
      author: 'Sebastian Münster',
      region: 'transilvania',
      imageUrl: 'https://customer-assets.emergentagent.com/job_f71459f5-332b-488f-a9b0-69ae068f31ee/artifacts/d016wt2a_NZ8_8906.jpg',
      description: 'O reprezentare cartografică detaliată a Transilvaniei din perioada Renașterii, evidențiind principalele orașe și formațiuni geografice ale regiunii.',
      fullDescription: 'Transsylvania XXI Nova Tabula este o hartă de referință pentru înțelegerea geografiei și organizării administrative a Transilvaniei în secolul al XVI-lea.',
      audioSrc: '/assets/audio/Transsylvania XXI Nova Tabula (Ed. II).mp3',
    },
    {
      id: 'NZ8_8950',  // Matches MongoDB ID for full description
      splineUrl: 'https://my.spline.design/untitled-KvKIUoPLEURXNlwSfoKnpcl6/',
      title: 'Nova Tabula Transylvaniae',
      subtitle: 'Noua Hartă a Transilvaniei',
      year: '1640',
      author: 'Willem & Joan Blaeu',
      region: 'transilvania',
      imageUrl: '/maps/NZ8_8950.jpg',
      description: 'Noua Hartă a Transilvaniei realizată de celebra casă cartografică olandeză Blaeu, oferind o perspectivă detaliată asupra regiunii.',
      fullDescription: 'Nova Tabula Transylvaniae de Willem & Joan Blaeu este considerată una dintre cele mai precise reprezentări ale regiunii din secolul al XVII-lea.',
      audioSrc: null,
      audioTrackId: 26, // Nova Tabula Transylvaniae din AudioContext
    },
    {
      id: 'NZ8_8880',  // Matches MongoDB ID
      splineUrl: 'https://my.spline.design/untitled-RKCPMAqeMoshjHruDMDKTai4/',
      title: 'TRANSYLVANIA',
      subtitle: 'Hartă Istorică',
      year: '1630',
      author: 'Gerard Mercator / Jodocus Hondius',
      region: 'transilvania',
      imageUrl: 'https://customer-assets.emergentagent.com/job_f71459f5-332b-488f-a9b0-69ae068f31ee/artifacts/pbiwmrgb_NZ8_8880.jpg',
      description: 'O reprezentare clasică a Transilvaniei, evidențiind granițele naturale formate de lanțul munților Carpați și principalele văi.',
      fullDescription: 'Această hartă a Transilvaniei oferă o imagine clară a regiunii, cu accent pe formațiunile geografice care au definit istoria acestui ținut.',
      audioSrc: '/assets/audio/TRANSYLVANIA.mp3',
    },
    {
      // Exemplarul vechi a fost donat ca premiu la Festival. Scena Spline a fost
      // republicata de proprietar cu exemplarul nou, pe aceeasi adresa, deci cardul
      // e inapoi in galeria 3D. Textura sta in scene.splinecode, pe serverele Spline,
      // nu in repo: daca mai apare vreodata exemplarul vechi, se republica scena.
      id: 'Z8L_5079',  // Matches MongoDB ID - Mercator map
      splineUrl: 'https://my.spline.design/untitled-7MMBTJTUdslCZD5b6KbOLOUz/',
      title: 'VALACHIA, SERVIA, BULGARIA, VND ROMANIA',
      subtitle: 'Valahia, Serbia, Bulgaria și Rumelia - Gerardus Mercator',
      year: '1589',
      author: 'Gerardus Mercator',
      region: 'valahia',
      // Rezerva folosita doar cand iframe-ul Spline nu se incarca (pe mobil).
      // Trebuie sa fie exemplarul NOU, nu cel donat.
      imageUrl: '/maps/Z8L_5079.jpg',
      description: 'O hartă cuprinzătoare a regiunilor balcanice, prezentând Valahia, Serbia, Bulgaria și teritoriile românești sub dominație otomană.',
      fullDescription: 'Această hartă germană oferă o perspectivă valoroasă asupra spațiului balcanic și a relațiilor dintre diferitele state și provincii din regiune în perioada otomană.',
      audioSrc: '/assets/audio/WALACHIA SERVIA BULGARIA - Homann.mp3',
    },
    {
      id: 'NZ8_8945',  // Matches MongoDB ID
      splineUrl: 'https://my.spline.design/untitled-8CFuSfsCfQo0qkN7ff6BIXGf/',
      title: 'Hungarie Descriptio',
      subtitle: 'Descrierea Ungariei',
      year: '1570',
      author: 'Wolfgang Lazius / Ortelius',
      region: 'ungaria',
      imageUrl: 'https://customer-assets.emergentagent.com/job_f71459f5-332b-488f-a9b0-69ae068f31ee/artifacts/lz5l9ys4_NZ8_8945.jpg',
      description: 'O hartă detaliată a Regatului Ungariei, incluzând teritoriile transilvanene și regiunile învecinate în perioada medievală târzie.',
      fullDescription: 'Hungarie Descriptio prezintă Regatul Ungariei în toată complexitatea sa geografică și politică, ilustrând atât teritoriile centrale cât și provinciile periferice.',
      audioSrc: null,
      audioTrackId: 54, // Hungariae Descriptio din AudioContext
    },
    {
      id: 'Z8W_9191',  // Matches MongoDB ID for full description
      splineUrl: 'https://my.spline.design/untitled-Mi2gSiZg7cuhiijL3dHNHZt3/',
      title: 'Transylvania - Hartă Mercator',
      subtitle: 'Transilvania în viziunea lui Mercator',
      year: '1595',
      author: 'Gerardus Mercator',
      region: 'transilvania',
      imageUrl: 'https://customer-assets.emergentagent.com/job_485114d5-d432-4824-9dce-6f422fc29e4f/artifacts/wl5hmqn0_Z8W_9191.jpg.jpeg',
      description: 'Harta Transilvaniei realizată de celebrul cartograf Gerardus Mercator, cunoscută pentru precizia și eleganța reprezentării cartografice.',
      fullDescription: 'Această hartă Mercator a Transilvaniei reprezintă standardul de aur al cartografiei renascentiste, combinând acuratețea științifică cu arta grafică.',
      audioSrc: '/assets/audio/Transylvania - Hartă Mercator.mp3',
    },
    {
      id: 'munster_cosmographia_1550',  // Matches MongoDB ID
      splineUrl: 'https://my.spline.design/untitled-VIWm4HGSaqLEpPA7yaCfJjwy/',
      title: 'Von Transsylvania, Siebenbürgen',
      subtitle: 'Cosmographia - Sebastian Münster',
      year: '1550',
      author: 'Sebastian Münster',
      region: 'valahia-moldova-transilvania',
      imageUrl: '/maps/munster_cosmographia_1550.jpg',
      description: 'Harta xilogravată din Cosmographia lui Sebastian Münster ilustrează relieful carpatic și așezările din Transilvania, Țara Românească și Bulgaria.',
      fullDescription: 'Analiza Istorică a Spațiului Carpato-Danubian în Secolul al XVI-lea, bazată pe Cosmographia lui Sebastian Münster, monumentala lucrare enciclopedică tipărită la Basel în 1544.',
      audioSrc: null,
    },
    {
      id: 'ORTELIUS_DACIARUM_1595_OL',  // Matches MongoDB ID
      splineUrl: 'https://my.spline.design/untitled-1pZpKGxLNvmfERinK6prcJsZ/',
      title: 'Tabula Nova Europae Orientalis et Meridionalis',
      subtitle: 'Spațiul Daco-Moesic în Antichitate - Waldseemüller, 1525',
      year: '1525',
      author: 'Waldseemüller',
      region: 'dacia',
      imageUrl: '/maps/daciarum-moesiarumque-ortelius.jpeg',
      description: 'Ediție timpurie a hărții Daciei și Moesiei, realizată de Abraham Ortelius și revizuită de Jodocus Hondius în 1595. Reprezintă spațiul daco-moesic în Antichitate, cu o coloratură caracteristică ediției olandeze, evidențiind rețeaua hidrografică și principalele cetăți antice.',
      fullDescription: 'Ediție timpurie a hărții Daciei și Moesiei, realizată de Abraham Ortelius și revizuită de Jodocus Hondius în 1595. Reprezintă spațiul daco-moesic în Antichitate, cu o coloratură caracteristică ediției olandeze, evidențiind rețeaua hidrografică și principalele cetăți antice.',
      audioSrc: null,
    },
    {
      id: 'ORTELIUS_DACIARUM_1603',
      splineUrl: 'https://my.spline.design/untitled-eyKKnVryG74TfInHD5U0J40b/',
      title: 'Daciarum, Moesiarumque, Vetus Descriptio 2',
      subtitle: 'Dacia și Moesia - Abraham Ortelius, Parergon 1603',
      year: '1603',
      author: 'Abraham Ortelius',
      region: 'dacia',
      imageUrl: '/maps/daciarum-moesiarumque-vetus.jpg',
      description: 'Harta Daciarum, Moesiarumque, Vetus Descriptio este o capodoperă a cartografiei Renașterii, realizată de Abraham Ortelius și publicată în faimosul atlas historic Parergon. Reprezintă Dacia și Moesia în viziunea Antichității, reconstituite pe baza surselor clasice.',
      fullDescription: 'Daciarum, Moesiarumque, Vetus Descriptio este o hartă istorică de excepție, publicată de Abraham Ortelius în Parergon, atlasul dedicat geografiei lumii antice, reconstituind granițele Daciei, Moesiei și teritoriilor adiacente.',
      audioSrc: null,
    },
    {
      id: 'VISSCHER_APOSTELEN_1660',
      splineUrl: 'https://my.spline.design/untitled-5i07QL6Mn5ZY3BzqB4ymmnVm/',
      title: 'Geographische Beschryvinghe van de Wandeling der Apostelen',
      subtitle: 'Călătoriile Apostolilor — Nicolaes Visscher I, Amsterdam c. 1660',
      year: '1660',
      author: 'Nicolaes Visscher I',
      region: 'terra-sancta',
      imageUrl: '/maps/114027.jpg',
      description: 'Lucrarea cartografică a lui Nicolaes Visscher I documentează vizual călătoriile misionare ale Apostolului Pavel prin bazinul estic al Mării Mediterane. Cele opt viniete decorative narează momente cruciale — convertirea pe drumul Damascului, naufragiul de la Malta, înfățișarea înaintea lui Nero — transformând harta într-un tratat teo-geografic unic.',
      fullDescription: 'Lucrarea cartografică a lui Nicolaes Visscher I documentează vizual călătoriile misionare ale Apostolului Pavel și ale celorlalți apostoli prin bazinul estic al Mării Mediterane, de la Roma până la Babilon. Familia Visscher a dominat piața de artă și cartografie din Amsterdam timp de un secol, remarcându-se prin acuratețe topografică și estetică inegalabilă a gravurilor.',
      audioSrc: '/audio/visscher-apostelen-1660.mp3',
    },
    {
      id: 'ORTELIUS_ROMANIAE_1588',
      splineUrl: 'https://my.spline.design/untitled-iLxgPWabJGJK8X19L6J4yMu6/',
      title: 'Romaniae, (quae olim Thracia dicta) Vicinarumque Regionum Descriptio',
      subtitle: 'Spațiul Balcanic și Românesc — Abraham Ortelius / Gastaldi, 1588',
      year: '1588',
      author: 'Abraham Ortelius / Giacomo Gastaldi',
      region: 'europa',
      imageUrl: '/maps/romaniae-quae-olim-thracia.jpg',
      description: 'Harta lui Abraham Ortelius bazată pe Giacomo Gastaldi reprezintă spațiul balcanic și românesc la sfârșitul secolului al XVI-lea, incluzând Thracia, Bulgaria, Valahia și Serbia. Document fundamental al cartografiei românești, fixează pentru prima dată cu precizie granițele Valahiei pe o hartă de autor european.',
      fullDescription: 'Harta Romaniae, quae olim Thracia dicta este realizată de Abraham Ortelius pe baza operei lui Giacomo Gastaldi și publicată în Theatrum Orbis Terrarum (1588). Reprezintă spațiul balcanic și românesc — Thracia, Bulgaria, Valahia, Serbia — cu o precizie remarcabilă pentru epoca Renașterii.',
      audioSrc: '/audio/ORTELIUS Romaniae, (quae olim Thracia dicta) Vicinarumque Regionum, Uti Bulgariae, Walachiae, Syrfiae AUDIO.mp3',
    },
    {
      id: 'MERCATOR_TAURICA_1595',
      splineUrl: 'https://my.spline.design/untitled-55468zgVBJM0adBo8uKE8C5k/',
      title: 'Taurica Chersonesus Nostra aetate Przecopsca et Gazara dicitur',
      subtitle: 'Hanatul Crimeei și Stepa Pontică în Cartografia Mercatoriană, 1595',
      year: '1595',
      author: 'Gerardus Mercator / Rumold Mercator',
      region: 'marea-neagra',
      imageUrl: '/maps/MERCATOR_TAURICA_1595.jpg',
      description: 'Capodoperă a cartografiei renascentiste târzii, harta Tauricei Chersonesos publicată postum de Rumold Mercator în 1595 înfățișează Hanatul Crimeei și stepa pontică la apogeul puterii otomano-tătare.',
      fullDescription: 'Harta Taurica Chersonesus, publicată postum de Rumold Mercator în 1595, reprezintă Peninsula Crimeea și stepa pontică în perioada de maximă influență a Hanatului Crimeei, vasal al Imperiului Otoman.',
      audioSrc: '/audio/Taurica Chersonesus.mp3',
    },
    {
      id: 'MUNSTER_CONSTANTINOPEL_1550',
      splineUrl: 'https://my.spline.design/untitled-wb8jvmLGSP30xIXf5pmykj1n/',
      title: 'Constantinopel — Vedere Panoramică din Cosmographia',
      subtitle: 'Întemeierea Noii Rome - Sebastian Münster, Basel',
      year: '1550-1578',
      author: 'Sebastian Münster',
      region: 'imperiu-otoman',
      imageUrl: '/maps/MUNSTER_CONSTANTINOPEL_1550.jpg',
      description: 'Vedere panoramică a Constantinopolului, xilogravură colorată manual din Cosmographia lui Sebastian Münster. Foaia însoțește biografiile împăraților Constantin, Constantius și Iulian și povestește întemeierea Noii Rome pe locul vechiului Byzantium.',
      fullDescription: 'Foaia din Cosmographia lui Sebastian Münster, tipărită la Basel, arată Constantinopolul dinspre apă și însoțește biografiile numerotate ale împăraților romani, de la Constantin cel Mare până la Iulian.',
      audioSrc: '/audio/Constantinopel - Cosmographia Munster.mp3',
    },
  ];

  // Mobile teaser component
  if (isMobile) {
    return (
      <MobileTeaser />
    );
  }

  // Desktop: render full showcases
  return (
    <div data-testid="3d-showcases-container">
      {showcases.map((showcase, index) => (
        <React.Fragment key={`${showcase.id}-${index}`}>
          <ShowcaseSection 
            showcase={showcase}
            onMapClick={onMapClick}
            index={index}
            audioData={audioData}
          />
          {/* Add divider between sections (not after last one) */}
          {index < showcases.length - 1 && <SectionDivider />}
        </React.Fragment>
      ))}

      {/* Global styles for dust particle animations */}
      <style>{`
        /* Hide inner scrollbars on panel containers */
        .panel-container {
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .panel-container::-webkit-scrollbar {
          display: none;
        }

        .dust-particle {
          position: absolute;
          width: 2px;
          height: 2px;
          background: rgba(198, 167, 94, 0.1);
          border-radius: 50%;
          pointer-events: none;
        }
        
        [class^="dust-1-"] {
          top: 20%;
          left: 30%;
          animation: float-dust 20s ease-in-out infinite;
        }
        
        [class^="dust-2-"] {
          top: 60%;
          left: 70%;
          animation: float-dust 25s ease-in-out infinite reverse;
          animation-delay: -5s;
        }
        
        [class^="dust-3-"] {
          top: 40%;
          left: 50%;
          animation: float-dust 18s ease-in-out infinite;
          animation-delay: -10s;
        }
        
        @keyframes float-dust {
          0%, 100% {
            transform: translate(0, 0) scale(1);
            opacity: 0.2;
          }
          25% {
            transform: translate(30px, -20px) scale(1.2);
            opacity: 0.4;
          }
          50% {
            transform: translate(-20px, 30px) scale(0.8);
            opacity: 0.15;
          }
          75% {
            transform: translate(40px, 10px) scale(1.1);
            opacity: 0.3;
          }
        }

        /* Mobile-specific styles */
        @media (max-width: 1023px) {
          .showcase-section {
            min-height: auto;
          }
          
          .showcase-content {
            min-height: auto;
          }
          
          .spline-container {
            /* Square aspect ratio on mobile for better 3D viewing */
            aspect-ratio: 1 / 1 !important;
            height: auto !important;
            min-height: 300px;
            max-height: 70vh;
            /* Prevent scroll interference */
            touch-action: none;
            -webkit-overflow-scrolling: auto;
          }
          
          .spline-container iframe {
            /* Ensure Spline captures all touch events */
            touch-action: none !important;
            pointer-events: auto;
          }
          
          .panel-container {
            padding: 24px 20px !important;
          }
          
          /* Smaller title on mobile */
          .panel-container h2 {
            font-size: 1.75rem !important;
          }
          
          /* Compact metadata on mobile */
          .panel-container .flex.items-center.gap-5 {
            gap: 12px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default Interactive3DShowcase;
